import { buildCalendar, type CalendarEvent } from "@hexling/core";
import { Button, EmptyState, Spinner } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import { useContent, useSettings } from "../../data/AppData";
import { addMonths, eventDate, formatDay, localDayKey, monthStart } from "../../lib/dates";

const KIND_LABEL: Record<CalendarEvent["kind"], string> = {
  lunar: "Moon",
  sabbat: "Sabbat",
  eclipse: "Eclipse",
  retrograde: "Retrograde",
};

/** A month at a time, as an agenda grouped by day (easier to read on a phone than a grid). */
export function CalendarPage({ today = new Date() }: { today?: Date }) {
  const content = useContent();
  const settings = useSettings();
  const [month, setMonth] = useState(() => monthStart(today));

  if (content.isPending || !settings) return <Spinner />;
  const index = content.data;
  if (!index)
    return (
      <EmptyState glyph="📅" title="Calendar data hasn't downloaded yet">
        Connect once and the moon tables for the next ten years will be saved on this device.
      </EmptyState>
    );

  const end = new Date(addMonths(month, 1).getTime() - 1);
  const events = buildCalendar(
    {
      lunar: index.lunar,
      sabbats: index.sabbatDates,
      eclipses: index.eclipses,
      retrogrades: index.retrogrades,
    },
    settings,
    month,
    end,
  );
  const days = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const d = eventDate(e.start);
    const key = d < month ? localDayKey(month) : localDayKey(d); // long events starting earlier show on day 1
    days.set(key, [...(days.get(key) ?? []), e]);
  }
  const todayKey = localDayKey(today);
  const anyOn =
    settings.features.lunar ||
    settings.features.sabbats ||
    settings.features.eclipses ||
    settings.features.retrogrades;

  return (
    <>
      <PageTitle
        title="Calendar"
        subtitle={`Times shown in your time zone. Sabbats use ${settings.sabbatDateStyle} dates for the ${settings.hemisphere} hemisphere.`}
      />
      <div className="month-nav">
        <Button
          variant="secondary"
          size="small"
          onClick={() => setMonth(addMonths(month, -1))}
          aria-label="Previous month"
        >
          ‹
        </Button>
        <h2 aria-live="polite">{formatDay(month, { month: "long", year: "numeric" })}</h2>
        <Button
          variant="secondary"
          size="small"
          onClick={() => setMonth(addMonths(month, 1))}
          aria-label="Next month"
        >
          ›
        </Button>
        {localDayKey(month) !== localDayKey(monthStart(today)) && (
          <Button variant="ghost" size="small" onClick={() => setMonth(monthStart(today))}>
            This month
          </Button>
        )}
      </div>

      {!anyOn ? (
        <EmptyState
          glyph="🌫️"
          title="Everything is hidden"
          action={<Link to="/settings">Choose what to show</Link>}
        >
          You've turned off moon phases, sabbats, eclipses and retrogrades.
        </EmptyState>
      ) : days.size === 0 ? (
        <EmptyState glyph="🌫️" title="A quiet month">
          Nothing you follow happens this month.
        </EmptyState>
      ) : (
        <ol className="agenda">
          {[...days.entries()].map(([key, list]) => (
            <li
              key={key}
              className={key === todayKey ? "agenda__day agenda__day--today" : "agenda__day"}
            >
              <h3>
                {formatDay(new Date(`${key}T12:00:00`), {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
                {key === todayKey && <span className="hx-muted"> (today)</span>}
              </h3>
              <ul>
                {list.map((e) => (
                  <li key={e.id} className={`event event--${e.kind}`}>
                    <span className="event__kind">{KIND_LABEL[e.kind]}</span>
                    <span>
                      {e.slug && (e.kind === "sabbat" || e.kind === "lunar") ? (
                        <Link
                          to="/library/$category/$slug"
                          params={{
                            category: e.kind === "sabbat" ? "sabbat" : "moon_phase",
                            slug: e.slug,
                          }}
                        >
                          {e.title}
                        </Link>
                      ) : (
                        e.title
                      )}
                      {e.start.length > 10 && (
                        <span className="hx-muted">
                          {" "}
                          at {formatDay(eventDate(e.start), { hour: "numeric", minute: "2-digit" })}
                        </span>
                      )}
                      {e.end && (
                        <span className="hx-muted">
                          {" "}
                          until {formatDay(eventDate(e.end), { month: "short", day: "numeric" })}
                        </span>
                      )}
                      {e.detail && <span className="hx-muted">, {e.detail.toLowerCase()}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
